import { File, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';

import { isImageFile, isPreviewableFile } from '@/lib/utils';

import { PdfViewer } from './pdf-viewer';

type DocumentPreviewContentProps = {
  previewUrl: string | undefined;
  isLoading: boolean;
  contentReady: boolean;
  fileExtension: string;
  fileName: string;
};

export function DocumentPreviewContent({
  previewUrl,
  isLoading,
  contentReady,
  fileExtension,
  fileName,
}: DocumentPreviewContentProps) {
  if (isLoading || !contentReady) {
    return (
      <div className="px-4 space-y-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-foreground">Document Preview</label>
          <File className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="rounded-lg border border-border bg-muted/10 p-8 flex flex-col items-center justify-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (previewUrl && isImageFile(fileExtension)) {
    return (
      <div className="flex-1 flex flex-col min-h-0 px-4 space-y-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-foreground">Document Preview</label>
          <ImageIcon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 rounded-lg border border-border bg-muted/20 p-4 overflow-hidden flex items-center justify-center">
          <img src={previewUrl} alt={fileName} className="max-w-full max-h-full object-contain" />
        </div>
      </div>
    );
  }

  if (previewUrl && fileExtension.toLowerCase() === 'pdf') {
    return (
      <div className="flex-1 flex flex-col min-h-0 px-4 space-y-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-foreground">Document Preview</label>
          <FileText className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 rounded-lg border border-border overflow-hidden">
          <PdfViewer url={previewUrl} />
        </div>
      </div>
    );
  }

  if (previewUrl && isPreviewableFile(fileExtension)) {
    return (
      <div className="flex-1 flex flex-col min-h-0 px-4 space-y-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-foreground">Document Preview</label>
          <FileText className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 rounded-lg border border-border bg-muted/20 overflow-hidden">
          <iframe
            src={previewUrl}
            className="w-full h-full border-0"
            title={fileName}
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 space-y-3">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-foreground">Document Preview</label>
        <File className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="rounded-lg border border-dashed border-border bg-muted/10 p-8 flex flex-col items-center justify-center gap-3 text-center">
        <File className="h-10 w-10 text-muted-foreground/40" />
        <div>
          <p className="text-sm font-medium text-muted-foreground">No content preview available</p>
          <p className="text-xs text-muted-foreground mt-1">This file type cannot be previewed</p>
        </div>
      </div>
    </div>
  );
}
